import type { Thunk, Action, Computed } from 'easy-peasy';
import { action, thunk, computed } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { StoreModel } from '../..';
import type { FetchAssetParams } from '../types';
import parseStatusCode from '../../errors/parseStatusCode';
import type { PartnerAddressV2, PartnerOrigin } from '@shipium-corp/shipium-ts-types';

export interface OriginModel {
  fetchOrigins: Thunk<OriginModel, FetchAssetParams, Injections, Promise<PartnerOrigin[]>>;
  fetchOrigin: Thunk<OriginModel, string, Injections, Promise<PartnerOrigin>>;
  originActionStart: Action<OriginModel, void>;
  lastAction?: number;
  origins: Record<string, PartnerOrigin>;
  originFailure: Action<OriginModel, ApplicationErrorOptions>;
  setOrigins: Action<OriginModel, PartnerOrigin[]>;
  getTimeZoneByOriginId: Computed<StoreModel, (originId: string) => string>;
}

export const originModel: OriginModel = {
  origins: {},
  originFailure: action((state, payload) => {
    console.error(payload);
  }),
  setOrigins: action((state, payload) => {
    for (const val of payload) {
      state.origins[val.originId] = val;
    }
  }),
  originActionStart: action((state, payload) => {
    state.lastAction = Date.now();
  }),
  fetchOrigins: thunk(async (actions, payload, { injections }) => {
    try {
      actions.originActionStart();
      const res = await injections.assetService.fetchOrigins(payload);
      actions.setOrigins(res);
      return res;
    } catch (error) {
      actions.originFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching origins',
        asNotification: true,
      });
    }
  }),
  fetchOrigin: thunk(async (actions, payload, { injections }) => {
    try {
      actions.originActionStart();
      const res = await injections.assetService.fetchOrigin(payload);
      actions.setOrigins([res]);
      return res;
    } catch (error) {
      actions.originFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching origin',
        asNotification: true,
      });
    }
  }),
  getTimeZoneByOriginId: computed(
    [(state: any, storeState: any) => state.origins, (state: any, storeState: any) => storeState.address.addresses],
    (origins: Record<string, PartnerOrigin>, addresses: Record<string, PartnerAddressV2>) => (originId: string) => {
      const address = addresses[origins?.[originId]?.addressId || ''];
      const timeZoneId = address?.location?.validatedAddressMetadata?.timeZoneId || 'Etc/Universal';
      return timeZoneId;
    },
  ),
};
